import { useUserStore } from '@/stores/userStore';
import fhirDao from '@/helperScripts/fhirHelpers/dao/fhirDao';
import { SearchQueryParentExtension } from '@/tsClasses/fhir/SearchQueryStore';
import { useFhirStore } from '@/stores/fhirStore';
import { usefilterProfileStore } from '@/stores/filterProfileStore';
import timestamper from '@/helperScripts/generalUtils/timestamper';

/**
 * @param searchQuery encoded filters
 * @param profileName name for filter profile
 * @returns true if successfully saved, false if not
 */
const saveFilterAsProfile = async (
  searchQuery: string,
  profileName: string
): Promise<boolean> => {
  const userQueryStore = await fhirDao.getSearchQueryStoreForUser(
    useUserStore().usrId
  );

  if (!profileName || profileName.trim().length === 0) {
    //We don't want empty names. --> Replace with Timestamp (instead of rejecting completely and throwing an error)
    profileName = timestamper.getIsoTimestampCurrentTimeZone();
  }

  if (userQueryStore !== undefined) {
    //Create new Extension for new search profile
    const newSearchProfile = new SearchQueryParentExtension(
      searchQuery,
      profileName
    );

    //Add to array of search profiles
    if (!userQueryStore.extension) {
      userQueryStore.extension = [];
    }
    userQueryStore.extension?.push(newSearchProfile);

    //Update resource on fhir server
    const putResponse = await fhirDao.putFhirResource(
      '/Basic/' + userQueryStore.id,
      userQueryStore
    );

    //Check success
    const requestSucessful =
      putResponse?.status >= 200 && putResponse?.status < 300;

    //update filter profiles in local store
    if (requestSucessful) {
      await usefilterProfileStore().updateSearchQueryStore();
    }

    //Check for success
    return requestSucessful;
  } else {
    throw new Error('Unable to find SearchQueryStore for current user.');
  }
};

const getName = (
  profileParentExtension: SearchQueryParentExtension
): undefined | string => {
  return profileParentExtension.extension?.find(
    (ext) => ext.url === useFhirStore().urls.searchQueryExtensionNameUrl
  )?.valueString;
};

const getQuery = (
  profileParentExtension: SearchQueryParentExtension
): undefined | string => {
  return profileParentExtension.extension?.find(
    (ext) => ext.url === useFhirStore().urls.searchQueryExtensionQueryUrl
  )?.valueString;
};

const getFilterProfilesForCurrentUser = async (): Promise<
  Array<SearchQueryParentExtension>
> => {
  const userSearchQueryStore = await fhirDao.getSearchQueryStoreForUser(
    useUserStore().usrId
  );
  return (userSearchQueryStore?.extension?.filter(
    (extension) => extension.url === useFhirStore().urls.searchQueryExtensionUrl
  ) ?? []) as Array<SearchQueryParentExtension>;
};

const deleteFilterProfile = async (nameOfProfileToDelete: string) => {
  console.log(`Deleting filter profile '${nameOfProfileToDelete}'.`);
  // Get current user query store
  const currentUserProfileStore = await fhirDao.getSearchQueryStoreForUser(
    useUserStore().usrId
  );
  if (!currentUserProfileStore) {
    console.warn(
      "Tried to get SearchQueryStore for current user but received undefined. Can't delete filter profile."
    );
    //Can't do anything if undefined
    return;
  }
  // Filter out extension with matching name
  currentUserProfileStore.extension = currentUserProfileStore.extension?.filter(
    (ext) =>
      !(ext as SearchQueryParentExtension).extension?.some(
        (subExt) =>
          subExt.url === useFhirStore().urls.searchQueryExtensionNameUrl &&
          subExt.valueString === nameOfProfileToDelete
      )
  );

  // PUT via fhirDao
  await fhirDao.putFhirResource(
    '/Basic/' + currentUserProfileStore.id,
    currentUserProfileStore
  );

  //update filter profiles in local store
  await usefilterProfileStore().updateSearchQueryStore();
};

/**
 * Checks whether a given name is among the names of the existing filter profiles for the current user.
 * @param filterName Name to look for
 * @returns True if a filter profile with given name existis, false otherwise.
 */
const userHasProfileWithName = (filterName: string): boolean => {
  return usefilterProfileStore().filterProfiles.some(
    (profile) =>
      getName(profile)?.toLowerCase() === filterName.toLocaleLowerCase()
  );
};

export default {
  saveFilterAsProfile,
  getName,
  getQuery,
  getFilterProfilesForCurrentUser,
  deleteFilterProfile,
  userHasProfileWithName,
};
